import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Eyebrow } from '../components/Eyebrow'
import {
  type PortConditions,
  type PortFacts,
  type PortHistoryPoint,
  type PortHoliday,
  type PortLive,
  type PortMeta,
  type StormThreat,
  fetchCyclones,
  fetchNextHolidays,
  fetchPortConditions,
  fetchPortHistory,
  fetchPortLive,
  fetchPortMeta,
  portStormThreat,
} from '../lib/ports-api'
import { usePageMeta } from '../lib/usePageMeta'
import { intensityStyle } from '../lib/weather-api'

const LEVEL_STYLE: Record<string, { color: string; label: string }> = {
  severe: { color: 'var(--danger)', label: 'Severe' },
  moderate: { color: 'var(--warn)', label: 'Moderate' },
  mild: { color: 'var(--signal)', label: 'Mild' },
}

export function PortPage() {
  const { code: rawCode } = useParams<{ code: string }>()
  const code = (rawCode ?? '').replace(/\.html$/i, '').toUpperCase()

  const [meta, setMeta] = useState<PortMeta | null>(null)
  const [threat, setThreat] = useState<StormThreat | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [weatherError, setWeatherError] = useState(false)
  const [live, setLive] = useState<PortLive | null>(null)
  // The open page answer has arrived (or failed): until then the congestion tile says "Checking…".
  const [liveDone, setLiveDone] = useState(false)
  const [history, setHistory] = useState<PortHistoryPoint[]>([])
  const [conditions, setConditions] = useState<PortConditions | null>(null)
  const [holidays, setHolidays] = useState<PortHoliday[]>([])

  const portName = meta?.name ?? `Port ${code}`

  usePageMeta(
    `${portName} port congestion & delays · SchedulesMCP`,
    `Live congestion level, storm exposure and affected shipping lanes for ${portName} (${code}). Fused from port-call throughput, AIS and weather feeds. Updated hourly.`
  )

  // THE LEVEL ONLY, from the open port answer (owner 07.10): wait, confidence and the narrative are the
  // keyed /public/port-congestion answer and are not on this page.
  useEffect(() => {
    let on = true
    if (!code) return
    setLiveDone(false)
    fetchPortMeta(code).then((m) => on && setMeta(m))
    fetchPortLive(code).then((l) => {
      if (!on) return
      setLive(l)
      setLiveDone(true)
    })
    fetchPortHistory(code).then((h) => on && setHistory(h))
    return () => {
      on = false
    }
  }, [code])

  // Country-holiday calendar (client-side, keyless), keyed on the ISO-2 country from the metadata.
  useEffect(() => {
    let on = true
    if (!meta?.cc) return
    fetchNextHolidays(meta.cc).then((h) => on && setHolidays(h))
    return () => {
      on = false
    }
  }, [meta?.cc])

  // Live marine/weather conditions (client-side, keyless), keyed on the port coords.
  useEffect(() => {
    let on = true
    if (meta?.lat == null || meta?.lng == null) return
    fetchPortConditions(meta.lat, meta.lng).then((c) => on && setConditions(c))
    return () => {
      on = false
    }
  }, [meta?.lat, meta?.lng])

  // biome-ignore lint/correctness/useExhaustiveDependencies: recompute when the port's coords arrive.
  useEffect(() => {
    let live = true
    if (!meta) return
    fetchCyclones()
      .then((cyc) => {
        if (live) {
          setThreat(portStormThreat({ lat: meta.lat, lon: meta.lng }, cyc))
          setWeatherError(false)
        }
      })
      // A weather-feed failure must NOT be presented as "all clear"; storm exposure was never
      // checked, so record the error and show a degraded/unknown note instead of "running normally".
      .catch(() => live && setWeatherError(true))
      .finally(() => live && setLoaded(true))
    return () => {
      live = false
    }
  }, [meta?.lat, meta?.lng])

  const lvl = live?.congestionLevel ? LEVEL_STYLE[live.congestionLevel] : null
  const origins = useMemo(() => meta?.lanes.filter((l) => l.role === 'origin') ?? [], [meta])
  const dests = useMemo(() => meta?.lanes.filter((l) => l.role === 'dest') ?? [], [meta])

  return (
    <main className="max-w-6xl mx-auto px-6 py-12">
      <nav className="text-sm text-[var(--text-muted)] mb-6">
        <Link to="/ports" className="no-underline text-[var(--text-secondary)] hover:text-signal">
          Ports
        </Link>{' '}
        <span className="text-[var(--text-faint)]">/</span>{' '}
        <span className="text-[var(--text-primary)]">{portName}</span>
      </nav>

      <div>
        <Eyebrow>Port intelligence</Eyebrow>
      </div>
      <h1
        className="font-display text-4xl sm:text-5xl leading-[1.05] text-[var(--text-primary)] mb-4"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        {portName} port congestion &amp; delays
      </h1>
      <p className="text-lg text-[var(--text-secondary)] max-w-2xl leading-relaxed mb-9">
        Live berth congestion level, active-storm exposure and the shipping lanes served by{' '}
        {portName}
        {meta?.cc ? ` (${meta.cc}, ${code})` : ` (${code})`}. Fused from{' '}
        <span className="text-[var(--text-primary)]">port-call throughput</span>, AIS dwell and the
        weather feeds, refreshed hourly.
      </p>

      {/* Live status band */}
      <div className="grid gap-4 sm:grid-cols-2 mb-6">
        <StatTile label="Congestion" accent={lvl?.color}>
          {!liveDone ? (
            <span className="text-[var(--text-muted)] text-base font-sans">Checking…</span>
          ) : lvl ? (
            <span style={{ color: lvl.color }}>{lvl.label}</span>
          ) : live ? (
            <span className="text-signal">Clear</span>
          ) : (
            <span className="font-mono">—</span>
          )}
        </StatTile>
        <StatTile label="Tracked vessels at port">
          <span className="font-mono">
            {live && live.vesselsAtPort > 0 ? live.vesselsAtPort.toLocaleString('en-US') : '—'}
          </span>
        </StatTile>
      </div>

      <CongestionSparkline points={history} />

      {(conditions || holidays.length > 0) && (
        <div className="flex flex-wrap gap-3 mb-6">
          {conditions && <ConditionsChip conditions={conditions} />}
          {holidays.length > 0 && <HolidayChip holiday={holidays[0] as PortHoliday} />}
        </div>
      )}

      {threat && <StormThreatCard threat={threat} portName={portName} />}

      {loaded && live && !lvl && !threat && !weatherError && (
        <div className="rounded-2xl border border-border bg-surface-0 p-6 mb-6 flex items-start gap-3">
          <span className="inline-block w-2 h-2 rounded-full bg-signal mt-2 shrink-0" />
          <p className="text-[var(--text-secondary)] leading-relaxed">
            No congestion or storm signal for {portName} right now. Berths and approaches are
            running normally.{' '}
            <span className="text-[var(--text-muted)]">This page updates hourly.</span>
          </p>
        </div>
      )}

      {loaded && live && !lvl && !threat && weatherError && (
        <div className="rounded-2xl border border-border bg-surface-0 p-6 mb-6 flex items-start gap-3">
          <span className="inline-block w-2 h-2 rounded-full bg-[var(--text-muted)] mt-2 shrink-0" />
          <p className="text-[var(--text-secondary)] leading-relaxed">
            No congestion signal for {portName} right now. Storm exposure could not be checked: the
            weather feed is temporarily unavailable.{' '}
            <span className="text-[var(--text-muted)]">This page updates hourly.</span>
          </p>
        </div>
      )}

      {meta?.facts && <PortFactsCard facts={meta.facts} />}

      {/* Served lanes */}
      {(origins.length > 0 || dests.length > 0) && (
        <section className="mt-12">
          <h2 className="font-display font-light tracking-[-0.015em] text-2xl text-[var(--text-primary)] mb-2">
            Shipping lanes through {portName}
          </h2>
          <p className="text-[var(--text-secondary)] mb-6 max-w-2xl leading-relaxed">
            When {portName} is congested or storm-exposed, these lanes carry the delay risk. Open a
            lane to compare carriers by real on-time reliability and transit time.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <LaneList title={`From ${portName}`} lanes={origins} />
            <LaneList title={`To ${portName}`} lanes={dests} />
          </div>
        </section>
      )}

      {/* CTA */}
      <div className="mt-12 flex flex-wrap gap-4">
        <Link
          to="/compare"
          className="bg-signal text-on-accent font-bold px-6 py-3 rounded-xl no-underline hover:opacity-90"
        >
          Compare carriers on a lane
        </Link>
        <Link
          to="/weather"
          className="border border-border px-6 py-3 rounded-xl no-underline hover:border-signal/50 text-[var(--text-primary)]"
        >
          Live storm map
        </Link>
      </div>

      <div className="mt-6">
        <a
          href={`https://trackingmcp.com/free-time?port=${code}`}
          className="text-sm text-[var(--text-secondary)] hover:text-signal no-underline"
        >
          Check demurrage free time at {portName} →
        </a>
      </div>
    </main>
  )
}

function fmtTeu(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M TEU`
  if (n >= 1e3) return `${Math.round(n / 1e3)}K TEU`
  return `${n} TEU`
}

function PortFactsCard({ facts }: { facts: PortFacts }) {
  const stats: Array<{ value: string; label: string }> = []
  if (facts.cppi_rank != null)
    stats.push({
      value: `#${facts.cppi_rank} of 405`,
      label: `Container Port Performance Index ${facts.cppi_year ?? 2023}`,
    })
  if (facts.plsci != null)
    stats.push({
      value: Math.round(facts.plsci).toLocaleString('en-US'),
      label: `UNCTAD connectivity · ${facts.plsci_quarter ?? '2026Q2'} · 100 = 2023 global avg`,
    })
  if (facts.teu_annual != null)
    stats.push({
      value: fmtTeu(facts.teu_annual),
      label: `Annual throughput${facts.teu_year ? ` (${facts.teu_year})` : ''}`,
    })
  if (facts.max_draft_m != null) stats.push({ value: `${facts.max_draft_m} m`, label: 'Max draft' })
  if (facts.berths != null) stats.push({ value: String(facts.berths), label: 'Berths' })

  const rows: Array<{ label: string; value: string }> = []
  if (facts.operator) rows.push({ label: 'Operator', value: facts.operator })
  if (facts.port_authority) rows.push({ label: 'Port authority', value: facts.port_authority })

  const terminals = Array.isArray(facts.terminals) ? facts.terminals : []

  if (stats.length === 0 && rows.length === 0 && terminals.length === 0) return null

  return (
    <section className="mt-12">
      <h2 className="font-display font-light tracking-[-0.015em] text-2xl text-[var(--text-primary)] mb-4">
        Port facts
      </h2>
      {stats.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-5">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl border border-border bg-surface-0 p-5">
              <div className="font-display font-light text-2xl text-[var(--text-primary)] tabular-nums">
                {s.value}
              </div>
              <div className="text-sm text-[var(--text-secondary)] mt-1.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}
      {rows.length > 0 && (
        <div className="mb-4">
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between gap-4 py-2.5 border-b border-border">
              <span className="text-sm text-[var(--text-secondary)]">{r.label}</span>
              <span className="text-sm text-[var(--text-primary)] text-right">{r.value}</span>
            </div>
          ))}
        </div>
      )}
      {terminals.length > 0 && (
        <div className="mb-3">
          <div className="text-xs uppercase tracking-wide text-[var(--text-muted)] font-mono mb-2.5">
            Terminals
          </div>
          <div className="flex flex-wrap gap-2">
            {terminals.map((t) => (
              <span
                key={t}
                className="inline-block rounded-full border border-border bg-surface-0 px-3 py-1.5 text-sm text-[var(--text-secondary)]"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="text-xs text-[var(--text-faint)] leading-relaxed mt-3">
        Sources: World Bank Container Port Performance Index 2023; UNCTAD PLSCI{' '}
        {facts.plsci_quarter ?? '2026Q2'};{' '}
        {facts.wikidata_id ? (
          <a
            href={`https://www.wikidata.org/wiki/${facts.wikidata_id}`}
            className="text-[var(--text-muted)] hover:text-signal no-underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Wikidata
          </a>
        ) : (
          'Wikidata'
        )}
      </div>
    </section>
  )
}

function StatTile({
  label,
  accent,
  children,
}: {
  label: string
  accent?: string | undefined
  children: ReactNode
}) {
  return (
    <div
      className="rounded-2xl border border-border bg-surface-0 p-5"
      style={accent ? { boxShadow: `inset 3px 0 0 ${accent}` } : undefined}
    >
      <div className="text-xs uppercase tracking-wide text-[var(--text-muted)] font-mono mb-2">
        {label}
      </div>
      <div className="font-display font-light text-3xl text-[var(--text-primary)] tabular-nums">
        {children}
      </div>
    </div>
  )
}

function CongestionSparkline({ points }: { points: PortHistoryPoint[] }) {
  // Keep only points with a numeric score; the trend needs at least two to draw a line.
  const scored = points.filter((p): p is PortHistoryPoint & { score: number } => p.score != null)
  if (scored.length < 2) return null

  const W = 640
  const H = 40
  const pad = 3
  const vals = scored.map((p) => p.score)
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  const span = max - min || 1
  const n = scored.length
  const x = (i: number) => pad + (i / (n - 1)) * (W - 2 * pad)
  const y = (v: number) => pad + (1 - (v - min) / span) * (H - 2 * pad)
  const d = scored
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(p.score).toFixed(1)}`)
    .join(' ')
  const last = scored[scored.length - 1] as PortHistoryPoint & { score: number }
  const color = (last.level && LEVEL_STYLE[last.level]?.color) || 'var(--signal)'

  return (
    <div className="rounded-2xl border border-border bg-surface-0 px-5 py-4 mb-6">
      <div className="flex items-center justify-between gap-4 mb-2">
        <span className="text-xs uppercase tracking-wide text-[var(--text-muted)] font-mono">
          90-day congestion trend
        </span>
        <span className="text-xs text-[var(--text-muted)] font-mono">
          latest {Math.round(last.score)}% · {n}d
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full h-10"
        role="img"
        aria-label={`Congestion density trend over the last ${n} days`}
      >
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={x(n - 1)} cy={y(last.score)} r={2.5} fill={color} />
      </svg>
    </div>
  )
}

function ConditionsChip({ conditions }: { conditions: PortConditions }) {
  const parts: string[] = []
  if (conditions.windKt != null) parts.push(`${Math.round(conditions.windKt)} kt wind`)
  if (conditions.gustKt != null && conditions.gustKt >= (conditions.windKt ?? 0) + 5)
    parts.push(`gust ${Math.round(conditions.gustKt)} kt`)
  if (conditions.waveM != null) parts.push(`${conditions.waveM.toFixed(1)} m swell`)
  if (parts.length === 0 && !conditions.fog) return null
  return (
    <div className="rounded-2xl border border-border bg-surface-0 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-[var(--text-muted)] font-mono mb-1">
        Conditions at port
      </div>
      <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
        <span className="font-mono">{parts.join(' · ')}</span>
        {conditions.fog && (
          <span
            className="inline-block rounded-full px-2 py-0.5 text-xs font-mono"
            style={{
              background: 'color-mix(in srgb, var(--warn) 15%, transparent)',
              color: 'var(--warn)',
            }}
          >
            fog &lt;1 km
          </span>
        )}
      </div>
    </div>
  )
}

function HolidayChip({ holiday }: { holiday: PortHoliday }) {
  const when = new Date(`${holiday.date}T00:00:00Z`)
  const label = Number.isNaN(when.getTime())
    ? holiday.date
    : when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  const name = holiday.localName || holiday.name
  return (
    <div className="rounded-2xl border border-border bg-surface-0 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-[var(--text-muted)] font-mono mb-1">
        Port may pause
      </div>
      <div className="text-sm text-[var(--text-primary)]">
        <span className="font-mono">{name}</span> on <span className="font-mono">{label}</span>
        <span className="block text-xs text-[var(--text-muted)] mt-0.5">
          Public holiday: terminals often keep skeleton operations rather than fully closing.
        </span>
      </div>
    </div>
  )
}

function StormThreatCard({ threat, portName }: { threat: StormThreat; portName: string }) {
  const { color, label } = intensityStyle(threat.category)
  const when = threat.whenIso ? new Date(threat.whenIso) : null
  return (
    <div
      className="rounded-2xl border p-6 mb-6"
      style={{ borderColor: color, background: `color-mix(in srgb, ${color} 8%, transparent)` }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        <span className="font-display font-bold text-lg text-[var(--text-primary)]">
          Storm exposure: {label} {threat.name}
        </span>
      </div>
      <p className="text-[var(--text-secondary)] leading-relaxed">
        {threat.name}'s forecast gale field reaches within{' '}
        <span className="font-mono text-[var(--text-primary)]">{threat.distanceKm} km</span> of{' '}
        {portName}
        {when ? ` around ${when.toUTCString().slice(5, 17)} UTC` : ' now'}. Expect berth and
        approach disruption. Sailings calling here are storm-exposed.
      </p>
    </div>
  )
}

function LaneList({
  title,
  lanes,
}: {
  title: string
  lanes: Array<{ slug: string; otherName: string; carriers: number; sailings: number }>
}) {
  if (lanes.length === 0) return null
  return (
    <div className="rounded-2xl border border-border bg-surface-0 p-5">
      <div className="text-xs uppercase tracking-wide text-[var(--text-muted)] font-mono mb-3">
        {title}
      </div>
      <ul className="space-y-0.5">
        {lanes.slice(0, 12).map((l) => (
          <li key={l.slug}>
            <Link
              to={`/lanes/${l.slug}`}
              className="flex items-center justify-between gap-3 no-underline text-[var(--text-secondary)] hover:text-signal py-1.5 group"
            >
              <span className="truncate">{l.otherName}</span>
              <span className="text-xs text-[var(--text-muted)] font-mono shrink-0 group-hover:text-signal">
                {l.carriers} carriers →
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
